<template>
  <div class="job-architecture-list-view">
    <div class="job-architecture-table-header architecture-row-grid">
      <div class="text-body-2 text-textDarkest">
        Name
      </div>
      <div class="text-body-2 text-textDarkest">
        # Families
      </div>
      <div class="text-body-2 text-textDarkest">
        # Profiles
      </div>
      <div class="text-body-2 text-textDarkest">
        Employees
      </div>
      <div class="text-body-2 text-textDarkest architecture-header-alerts">
        Alerts
      </div>
    </div>
    <div class="job-architecture-list-view-tree">
      <JobArchitectureTreeview
        v-model:selected="selectedProfiles"
        :items="treeItems"
        item-value="value"
        item-title="title"
        item-children="children"
        selectable
      >
        <template #item-label="{ item }">
          {{ item.title }}
        </template>
      </JobArchitectureTreeview>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import {
  groupId,
  familyNodeId,
  getGroupArchitectureStats,
  getGroupTotalProfileCount,
  getFamilyArchitectureStats,
  formatArchitectureFamilyCountLabel,
  formatArchitectureProfilesCountLabel,
} from '@/components/jobArchitecture/helpers';
import JobArchitectureTreeview from '@/components/shared/JobArchitectureTreeview.vue';

const props = defineProps({
  treeData: { type: Object, default: null },
});

const selectedProfiles = defineModel('selectedProfiles', {
  type: Array,
  default: () => [],
});

const treeItems = computed(() => {
  const groups = props.treeData?.groups;

  if (!groups?.length) return [];

  return groups.map((group) => {
    const families = group.families || [];
    const groupStats = getGroupArchitectureStats(group);
    const profileTotal = getGroupTotalProfileCount(group);

    return {
      title: group.name || 'Uncategorized',
      value: groupId(group),
      structureFamiliesLabel: formatArchitectureFamilyCountLabel(groupStats.familyCount),
      structureProfilesLabel: formatArchitectureProfilesCountLabel(profileTotal),
      employeesDisplay: groupStats.employeeCount,
      children: families.map((family) => {
        const profiles = family.profiles || [];
        const famStats = getFamilyArchitectureStats(family);
        const profileLabel = `${famStats.profileCount} job profile${famStats.profileCount === 1 ? '' : 's'}`;

        return {
          title: family.name || 'Uncategorized',
          value: familyNodeId(group, family),
          structureProfilesLabel: profileLabel,
          employeesDisplay: famStats.employeeCount,
          children: profiles.map((profile) => ({
            title: profile.title || '',
            value: profile.ulid,
            employeesDisplay: profile.employee_count || 0,
            jobLevel: profile.job_level || null,
          })),
        };
      }),
    };
  });
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-list-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.architecture-row-grid {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) 88px 150px;
  column-gap: 12px;
  padding-left: 43px;
  padding-right: 16px;
}

.job-architecture-table-header {
  position: sticky;
  top: 96px;
  z-index: 2;
  height: 40px;
  border-bottom: 1px solid @stroke-default;
  border-top: 1px solid @stroke-default;
  background-color: @bg-canvas;
  border-radius: 8px 8px 0 0;
}

.job-architecture-list-view-tree {
  padding: 12px 0;
}

.architecture-header-alerts {
  width: 150px;
  min-width: 150px;
  padding-left: 4px;
}
</style>
